/**
 * LE TRAIT DU PROF — de l'annotation, dans la marge, au morceau exact de la
 * formule dont elle parle (MV-SAV-03, bloc `exemple_annote`).
 *
 * Géométrie pure, partagée par le rendu natif et celui de la console : on lui
 * donne la boîte de la cible et le point d'où part l'écriture, dans le même
 * repère (celui de la copie), elle rend trois tracés — la marque (un
 * soulignement ou un tour de stylo), la courbe, la pointe.
 *
 * La main est celle de la maquette (`kit/ExempleAnnote.tsx`) : un soulignement
 * presque droit, jamais tout à fait ; un tour de stylo qui ne se referme pas
 * exactement ; une courbe qui contourne la formule au lieu de la traverser.
 */
const f = (n) => n.toFixed(1);
function souligner(c, rang) {
    const y = c.b + 3;
    const x0 = c.g - 2;
    const x1 = c.d + 3;
    const creux = rang % 2 === 0 ? 2.2 : -1.6;
    return {
        d: `M${f(x0)} ${f(y + 0.6)} Q${f((x0 + x1) / 2)} ${f(y + creux)} ${f(x1)} ${f(y - 0.4)}`,
        arrivee: [(x0 + x1) / 2, y + 1],
    };
}
function entourer(c) {
    const cx = (c.g + c.d) / 2;
    const cy = (c.h + c.b) / 2;
    const rx = (c.d - c.g) / 2 + 5;
    const ry = (c.b - c.h) / 2 + 4;
    const debut = -2.5;
    const tour = Math.PI * 2 + 0.45;
    const pas = 44;
    const morceaux = [];
    for (let i = 0; i <= pas; i += 1) {
        const t = debut + (tour * i) / pas;
        const gonfle = 1 + 0.07 * (i / pas);
        const x = cx + rx * gonfle * Math.cos(t);
        const y = cy + ry * gonfle * Math.sin(t);
        morceaux.push(`${i === 0 ? 'M' : 'L'}${f(x)} ${f(y)}`);
    }
    return { d: morceaux.join(' '), rx, ry, cx, cy };
}
function pointeDeFleche(vers, depuis) {
    const angle = Math.atan2(vers[1] - depuis[1], vers[0] - depuis[0]);
    const long = 8.5;
    const ouverture = 0.46;
    const a1 = angle + Math.PI - ouverture;
    const a2 = angle + Math.PI + ouverture;
    return (`M${f(vers[0] + long * Math.cos(a1))} ${f(vers[1] + long * Math.sin(a1))} ` +
        `L${f(vers[0])} ${f(vers[1])} ` +
        `L${f(vers[0] + long * Math.cos(a2))} ${f(vers[1] + long * Math.sin(a2))}`);
}
/**
 * Le trait d'une annotation.
 *
 * @param cible  la boîte du morceau visé
 * @param depart le bord gauche de l'écriture, à mi-hauteur de sa ligne
 * @param rang   le rang de l'annotation dans l'exemple : il fait alterner la
 *               courbure, pour que deux traits voisins ne soient pas jumeaux
 */
export function tracerAnnotation(cible, depart, marque, par, rang) {
    const c = cible;
    let dMarque = '';
    let arrivee;
    const cx = (c.g + c.d) / 2;
    const cy = (c.h + c.b) / 2;
    if (marque === 'entoure') {
        const o = entourer(c);
        dMarque = o.d;
        arrivee =
            par === 'droite'
                ? [o.cx + o.rx + 5, o.cy + 1]
                : par === 'dessus'
                    ? [o.cx + o.rx * 0.35, o.cy - o.ry - 4]
                    : [o.cx + o.rx * 0.35, o.cy + o.ry + 4];
    }
    else if (marque === 'souligne') {
        const s = souligner(c, rang);
        dMarque = s.d;
        arrivee =
            par === 'droite'
                ? [c.d + 8, cy + 1]
                : par === 'dessus'
                    ? [cx + (c.d - c.g) * 0.15, c.h - 6]
                    : [s.arrivee[0] + (c.d - c.g) * 0.18, s.arrivee[1] + 5];
    }
    else {
        arrivee =
            par === 'droite' ? [c.d + 8, cy + 1] : par === 'dessus' ? [cx, c.h - 6] : [cx, c.b + 6];
    }
    const ecart = Math.max(24, depart[0] - arrivee[0]);
    let p1;
    let p2;
    if (par === 'droite') {
        const bombe = rang % 2 === 0 ? -9 : 9;
        p1 = [depart[0] - ecart * 0.35, depart[1] + bombe];
        p2 = [arrivee[0] + ecart * 0.3, arrivee[1] + bombe * 0.6];
    }
    else if (par === 'dessus') {
        const haut = Math.min(depart[1], arrivee[1]) - 14;
        p1 = [depart[0] - ecart * 0.4, haut - 4];
        p2 = [arrivee[0] + Math.min(16, ecart * 0.12), arrivee[1] - 30];
    }
    else {
        const bas = Math.max(depart[1], arrivee[1]) + 5;
        p1 = [depart[0] - ecart * 0.45, bas + 4];
        p2 = [arrivee[0] + Math.min(30, ecart * 0.2), arrivee[1] + 19];
    }
    return {
        marque: dMarque,
        fleche: `M${f(depart[0])} ${f(depart[1])} C${f(p1[0])} ${f(p1[1])} ` +
            `${f(p2[0])} ${f(p2[1])} ${f(arrivee[0])} ${f(arrivee[1])}`,
        pointe: pointeDeFleche(arrivee, p2),
    };
}
/** Le seul soulignement d'une cible : la mise en page empilée (écran étroit) n'a pas de trait. */
export function soulignerCible(cible, rang) {
    return souligner(cible, rang).d;
}
/**
 * Découpe l'écriture du prof en morceaux : du texte, et ce que `^{…}` met en
 * exposant. « 5^{−2}, c'est 1 sur 5^{2}. » → 5, ⁻², « , c'est 1 sur 5 », ², « . »
 */
export function decouperManuscrit(texte) {
    return texte
        .split(/(\^\{[^}]*\})/g)
        .filter((m) => m.length > 0)
        .map((m) => m.startsWith('^{')
        ? { texte: m.slice(2, -1), exposant: true }
        : { texte: m, exposant: false });
}
//# sourceMappingURL=annotation.js.map